import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Chip,
  Skeleton,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  alpha,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";
import {
  useGetPendingRequestsQuery,
  useHandleJoinRequestMutation,
  type PendingJoinRequest,
} from "components/Slices/circleApiSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

interface PendingRequestsSectionProps {
  groupId: string;
  isAdmin: boolean;
}

const RequestItem: React.FC<{
  request: PendingJoinRequest;
  onApprove: (userId: string) => void;
  onDecline: (userId: string) => void;
  isProcessing: boolean;
}> = ({ request, onApprove, onDecline, isProcessing }) => {
  const theme = useTheme();

  return (
    <ListItem
      sx={{
        py: 2,
        px: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.04),
        borderRadius: 2,
        mb: 1,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
      }}
    >
      <ListItemAvatar>
        <Avatar
          src={request.User.displayImg || undefined}
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 48,
            height: 48,
          }}
        >
          {request.User.firstName?.charAt(0).toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle1" fontWeight={600}>
            {request.User.firstName} {request.User.lastName}
          </Typography>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {request.User.email}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontStyle: "italic" }}
            >
              Requested {dayjs(request.createdAt).fromNow()}
            </Typography>
          </Box>
        }
      />
      <Stack direction="row" spacing={1}>
        <Tooltip title="Approve">
          <IconButton
            color="success"
            onClick={() => onApprove(request.User.id)}
            disabled={isProcessing}
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.success.main, 0.2),
              },
            }}
          >
            <CheckIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Decline">
          <IconButton
            color="error"
            onClick={() => onDecline(request.User.id)}
            disabled={isProcessing}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </ListItem>
  );
};

const LoadingSkeleton: React.FC = () => (
  <List disablePadding>
    {[1, 2, 3].map((i) => (
      <ListItem key={i} sx={{ py: 2 }}>
        <ListItemAvatar>
          <Skeleton variant="circular" width={48} height={48} />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton width="60%" height={24} />}
          secondary={<Skeleton width="40%" height={20} />}
        />
        <Stack direction="row" spacing={1}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
        </Stack>
      </ListItem>
    ))}
  </List>
);

const PendingRequestsSection: React.FC<PendingRequestsSectionProps> = ({
  groupId,
  isAdmin,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [processingUsers, setProcessingUsers] = useState<Set<string>>(
    new Set()
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: requests, isLoading } = useGetPendingRequestsQuery(
    { groupId },
    { skip: !isAdmin }
  );

  const [handleJoinRequest] = useHandleJoinRequestMutation();

  const handleAction = useCallback(
    async (userId: string, decision: "accepted" | "rejected") => {
      setProcessingUsers((prev) => new Set(prev).add(userId));

      try {
        await handleJoinRequest({ groupId, userId, decision }).unwrap();
        setSnackbar({
          open: true,
          message:
            decision === "accepted"
              ? "Member approved! They've been added to the circle."
              : "Request declined.",
          severity: "success",
        });
      } catch (error: any) {
        const message =
          error?.data?.message || "Failed to process request. Please try again.";
        setSnackbar({
          open: true,
          message,
          severity: "error",
        });
      } finally {
        setProcessingUsers((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [groupId, handleJoinRequest]
  );

  // Don't show for non-admins
  if (!isAdmin) {
    return null;
  }

  // Don't show if no pending requests
  if (!isLoading && (!requests || requests.length === 0)) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        overflow: "hidden",
        mb: 4,
        bgcolor: alpha(theme.palette.warning.main, 0.02),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            bgcolor: alpha(theme.palette.warning.main, 0.05),
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonAddIcon sx={{ color: "warning.main" }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Pending Join Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and approve or decline member requests
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={isLoading ? "..." : requests?.length || 0}
            color="warning"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <List disablePadding>
              {requests?.map((request) => (
                <RequestItem
                  key={request.id}
                  request={request}
                  onApprove={(userId) => handleAction(userId, "accepted")}
                  onDecline={(userId) => handleAction(userId, "rejected")}
                  isProcessing={processingUsers.has(request.User.id)}
                />
              ))}
            </List>
          )}
        </Box>
      </Collapse>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PendingRequestsSection;

